import React, { useState } from "react";
import { Box, Typography, makeStyles, Chip, Switch } from "@material-ui/core";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import CustomDatePicker from "components/Input/CustomDatePicker";
import SelectField from "components/Select/SelectField";
import AppColor from "utilities/AppColor";
import { createPublicComplaint } from "store/slices/complaint";

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "100vh",
    background: "#fff",
    padding: theme.spacing(3),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(5),
    },
  },
  card: {
    background: "#fff",
    borderRadius: "10px",
    padding: "30px",
    maxWidth: "600px",
    margin: "0 auto",
    boxShadow: "0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)",
  },
  title: {
    color: "#1a1a18",
    fontSize: "24px",
    fontWeight: "500",
    textAlign: "center",
    marginBottom: "5px",
    marginTop: 0,
  },
  subtitle: {
    color: "#888780",
    fontSize: "13px",
    textAlign: "center",
    marginBottom: "25px",
  },
  pillGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "16px",
  },
  pill: {
    fontSize: "12px",
    padding: "4px 12px",
    borderRadius: "20px",
    border: "0.5px solid #c5c4bc",
    color: "#5f5e5a",
    cursor: "pointer",
    userSelect: "none",
  },
  selectedPill: {
    border: "1.5px solid #1D9E75",
    background: "#E1F5EE",
    color: "#0F6E56",
    fontWeight: 500,
  },
  sectionBox: {
    background: "#fff",
    borderRadius: "8px",
    border: "0.5px solid #e5e5e2",
    padding: "20px",
    marginBottom: "20px",
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#1a1a18",
    marginBottom: "15px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  infoBox: {
    background: "#F0FDFA",
    border: "1px solid #99F6E4",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "12px",
    color: "#0D9488",
    lineHeight: "1.5",
    marginBottom: "20px",
  },
  warningBox: {
    background: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "12px",
    color: "#92400E",
    lineHeight: "1.5",
    marginBottom: "15px",
  },
  control: {
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
  },
  switchRoot: {
    width: "48px",
    height: "24px",
    padding: "0px",
    '& .MuiSwitch-switchBase': {
      padding: "2px",
      color: "#fff",
      '&.Mui-checked': {
        transform: 'translateX(24px)',
        color: "#fff",
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: "#4ADE80",
          border: "none",
        },
      },
    },
    '& .MuiSwitch-thumb': {
      width: "20px",
      height: "20px",
      boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.12), 0px 4px 5px 0px rgba(0,0,0,0.08)",
    },
    '& .MuiSwitch-track': {
      borderRadius: "16px",
      opacity: 1,
      backgroundColor: "#E5E7EB",
      border: "none",
    },
  },
}));

const whoAreYouOptions = [
  { value: "participant", label: "Participant" },
  { value: "family", label: "Family / guardian" },
  { value: "worker", label: "Worker" },
  { value: "other", label: "Other" },
];

const relationshipOptions = [
  { value: "", label: "Select relationship" },
  { value: "support_worker", label: "Support worker" },
  { value: "team_leader", label: "Team Leader / Coordinator" },
  { value: "participant", label: "Another participant" },
  { value: "worker", label: "Another worker" },
  { value: "organisation", label: "Organisation / service generally" },
];

const PublicComplaintForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const domain = queryParams.get("domain") || window.location.hostname;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");

  const getValidationSchema = (values) => {
    return Yup.object().shape({
      submitterType: Yup.string().required("Please select who you are"),
      fullName: values.anonymous ? Yup.string() : Yup.string().required("Full name is required"),
      phone: values.anonymous ? Yup.string() : Yup.string().required("Phone is required"),
      employeeId: (values.submitterType === "worker" && !values.anonymous) 
        ? Yup.string().required("Employee ID is required") 
        : Yup.string(),
      email: Yup.string().email("Invalid email"),
      complaintAbout: Yup.string().required("This field is required"),
      relationship: Yup.string().required("Relationship is required"),
      dateOfEvent: Yup.string().required("Date is required"),
      description: Yup.string().required("Description is required"),
      desiredOutcome: Yup.string().required("Desired outcome is required"),
    });
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        domain,
        complaintBy: values.submitterType === "family" ? "FAMILY_GUARDIAN" : values.submitterType.toUpperCase(),
        isAnonymous: values.anonymous,
        complaintName: values.anonymous ? null : values.fullName,
        complaintEmployeeId: (values.submitterType === "worker" && !values.anonymous) ? values.employeeId : null,
        complaintPhoneNumber: values.anonymous ? null : values.phone,
        complaintEmail: values.anonymous ? null : (values.email || null),
        complaintAbout: values.complaintAbout,
        complaintRelationship: {
          support_worker: "SUPPORT_WORKER",
          team_leader: "TEAM_LEADER_COORDINATOR",
          participant: "ANOTHER_PARTICIPANT",
          worker: "ANOTHER_WORKER",
          organisation: "ORGANISATION_SERVICE_GENERALLY"
        }[values.relationship] || "ORGANISATION_SERVICE_GENERALLY",
        incidentDate: `${values.dateOfEvent} 00:00`,
        complaintDescription: values.description,
        complaintDesiredOutcome: values.desiredOutcome || ""
      };

      const res = await dispatch(createPublicComplaint(payload));
      if (res && res.payload) {
        setReferenceNumber(res.payload.complaintNumber);
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error("Failed to submit complaint:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Box style={{ 
        minHeight: "100vh", 
        background: "#f5f5f3", 
        display: "flex", 
        alignItems: "flex-start", 
        justifyContent: "center", 
        padding: "40px 16px" 
      }}>
        <Box style={{ 
          background: "#fff", 
          borderRadius: "12px", 
          border: "0.5px solid #e5e5e2", 
          width: "100%", 
          maxWidth: "560px", 
          overflow: "hidden", 
          padding: "30px 24px"
        }}>
          <Box
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "#F0FDFA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <i className="fas fa-check" style={{ fontSize: "36px", color: "#0D9488" }}></i>
          </Box>
          <Typography style={{ fontSize: "22px", fontWeight: 700, color: "#1a1a18", marginBottom: "12px", textAlign: "center" }}>
            Thank you for your feedback
          </Typography>
          <Typography style={{ fontSize: "14px", color: "#6B7280", lineHeight: 1.6, marginBottom: "24px", textAlign: "center" }}>
            Your complaint has been logged and will be reviewed by management. Complaints are acknowledged within 2 business days.
          </Typography>
          
          {/* Reference Number Box */}
          <Box style={{ 
            background: "#fff", 
            border: "1px solid #E5E7EB", 
            borderRadius: "12px", 
            padding: "18px", 
            marginBottom: "24px", 
            textAlign: "left" 
          }}>
            <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <i className="far fa-id-badge" style={{ fontSize: "18px", color: "#0D9488" }}></i>
              <Typography style={{ fontSize: "12px", fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                Reference Number
              </Typography>
            </Box>
            <Typography style={{ fontSize: "22px", fontWeight: 800, color: "#0D9488", marginBottom: "8px" }}>
              {referenceNumber}
            </Typography>
            <Typography style={{ fontSize: "13px", color: "#6B7280" }}>
              Keep this reference if you follow up by phone or email.
            </Typography>
          </Box>

          {/* What Happens Next Box */}
          <Box style={{ 
            background: "#fff", 
            border: "1px solid #E5E7EB", 
            borderRadius: "12px", 
            padding: "18px", 
            marginBottom: "28px", 
            textAlign: "left" 
          }}>
            <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <i className="fas fa-info-circle" style={{ fontSize: "18px", color: "#0D9488" }}></i>
              <Typography style={{ fontSize: "12px", fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                What happens next
              </Typography>
            </Box>
            <Box component="ol" style={{ margin: 0, paddingLeft: "20px", fontSize: "14px", color: "#1a1a18", lineHeight: "1.8" }}>
              <li>Acknowledged within 2 business days</li>
              <li>Reviewed and investigated</li>
              <li>Resolved with an outcome — upheld, partially upheld, or not upheld</li>
              <li>You&apos;re notified of the outcome (if contact details provided)</li>
            </Box>
          </Box>

          <AppButton
            label="Done"
            onClick={() => window.location.href = "/auth/company/login"}
            buttonColor={AppColor.mainColor}
            rounder
            style={{ 
              width: "100%", 
              padding: "14px 24px", 
              fontSize: "15px", 
              fontWeight: 600,
              borderRadius: "12px" 
            }}
            icon={<i className="fas fa-home" style={{ marginRight: "8px" }}></i>}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <h5 className={classes.title}>Submit a complaint</h5>
      {/* <Typography className={classes.subtitle}>Care Aid</Typography> */}

      <Box className={classes.card}>
        <Box className={classes.infoBox}>
          This form is for feedback or concerns about the service you&apos;ve received. It is separate from incident reporting. All complaints are taken seriously and reviewed by management.
        </Box>

        <Formik
          initialValues={{
            submitterType: "participant",
            anonymous: false,
            fullName: "",
            employeeId: "",
            phone: "",
            email: "",
            complaintAbout: "",
            relationship: "",
            dateOfEvent: "",
            description: "",
            desiredOutcome: "",
          }}
          validate={(values) => {
            const schema = getValidationSchema(values);
            try {
              schema.validateSync(values, { abortEarly: false });
              return {};
            } catch (err) {
              const errors = {};
              err.inner.forEach((error) => {
                errors[error.path] = error.message;
              });
              return errors;
            }
          }}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Form>
              <Box className={classes.sectionBox}>
                <Typography className={classes.sectionTitle}>
                  <i className="fas fa-user-circle"></i> Who are you?
                </Typography>
                <Box className={classes.pillGroup}>
                  {whoAreYouOptions.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      className={`${classes.pill} ${
                        values.submitterType === option.value ? classes.selectedPill : ""
                      }`}
                      onClick={() => setFieldValue("submitterType", option.value)}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>

              <Box className={classes.sectionBox}>
                <Typography className={classes.sectionTitle}>
                  <i className="fas fa-id-badge"></i> Your details
                </Typography>
                
                {
                  values.submitterType !== "worker" &&
                  <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="15px">
                  <Typography style={{ fontSize: "12px", fontWeight: 500, color: "#374151" }}>
                    Submit anonymously
                  </Typography>
                  <Switch
                    checked={values.anonymous}
                    onChange={(e) => setFieldValue("anonymous", e.target.checked)}
                    classes={{
                      root: classes.switchRoot,
                    }}
                    name="anonymous"
                  />
                </Box>
                }

                {values.anonymous && values.submitterType !== "worker"? (
                  <Box className={classes.warningBox}>
                    <i className="fas fa-exclamation-circle"></i> You won&apos;t be contacted about the outcome of this complaint since no contact details will be recorded.
                  </Box>
                ) : (
                  <>
                    <Typography style={{ fontSize: "11px", color: "#6B7280", marginBottom: "15px" }}>
                      Providing your details lets us contact you about the outcome. Anonymous complaints are still investigated but we can&apos;t follow up with you directly.
                    </Typography>

                    {values.submitterType === "worker" && (
                      <Box className={classes.infoBox} style={{ background: "#F0FDFA", borderColor: "#99F6E4", color: "#0D9488" }}>
                        <i className="fas fa-info-circle"></i> Scanning the QR code doesn&apos;t log you in — enter your name and Employee ID manually so this can be matched to your staff record.
                      </Box>
                    )}

                    <div className="row">
                      <div className="col-sm-12 col-md-12 mb-3">
                        <Field
                          component={AppInput}
                          name="fullName"
                          label="Full name"
                          required
                          placeholder="Your name"
                        />
                      </div>
                    </div>

                    {values.submitterType === "worker" && (
                      <div className="row">
                        <div className="col-sm-12 col-md-12 mb-3">
                          <Field
                            component={AppInput}
                            name="employeeId"
                            label="Employee ID"
                            required
                            placeholder="e.g. CA-1042"
                          />
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-sm-12 col-md-6 mb-3">
                        <Field
                          component={AppInput}
                          name="phone"
                          label="Phone"
                          required
                          placeholder="04xx xxx xxx"
                        />
                      </div>
                      <div className="col-sm-12 col-md-6 mb-3">
                        <Field
                          component={AppInput}
                          name="email"
                          label="Email"
                          placeholder="you@email.com"
                        />
                      </div>
                    </div>
                  </>
                )}
              </Box>

              <Box className={classes.sectionBox}>
                <Typography className={classes.sectionTitle}>
                  <i className="fas fa-users"></i> Who is this complaint about?
                </Typography>
                
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <Field
                      component={AppInput}
                      name="complaintAbout"
                      label="Name of worker, participant, or team"
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-12 mb-3">
                    <Field
                      component={SelectField}
                      name="relationship"
                      label="Relationship to Care Aid"
                      options={relationshipOptions}
                    />
                  </div>
                </div>
              </Box>

              <Box className={classes.sectionBox}>
                <Typography className={classes.sectionTitle}>
                  <i className="fas fa-calendar-alt"></i> When did it happen?
                </Typography>
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <Field
                      component={CustomDatePicker}
                      label="Date of event"
                      name="dateOfEvent"
                      type="text"
                      formatDateJob={true}
                      required
                      classInput="date-picker-container-custom"
                    />
                  </div>
                </div>
              </Box>

              <Box className={classes.sectionBox}>
                <Typography className={classes.sectionTitle}>
                  <i className="fas fa-comment-dots"></i> Description of the complaint <span style={{ color: "#DC2626", marginLeft: "4px" }}>*</span>
                </Typography>
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <Field
                      component={AppInput}
                      name="description"
                      multiline
                      rows={5}
                      type="textarea"
                      placeholder="Tell us what happened, in as much detail as you can."
                    />
                    
                  </div>
                </div>
              </Box>

              <Box className={classes.sectionBox} style={{ marginBottom: "10px" }}>
                <Typography className={classes.sectionTitle}>
                  <i className="fas fa-bullseye"></i> Desired outcome
                </Typography>
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <Field
                      component={AppInput}
                      name="desiredOutcome"
                      multiline
                      rows={5}
                      type="textarea"
                      placeholder="What would you like to happen as a result of this complaint?"
                    />
                  </div>
                </div>
              </Box>

              <Box className={classes.control}>
                <AppButton
                  label="Submit complaint"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  buttonColor={AppColor.mainColor}
                  rounder
                  style={{ width: "100%", padding: "12px", fontSize: "14px", fontWeight: 600 }}
                  icon={<i className="fas fa-paper-plane" style={{ marginRight: "8px" }}></i>}
                />
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default PublicComplaintForm;
